// @noflow
import { ACCOUNT_ROUTES } from '@/routes'
import classNames from 'classnames'
import React, { useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import BREAKPOINTS from '@/constants/Breakpoints'

import useWindowSize from '@/hooks/useWindowSize'

import { Button } from '@/components/elements/atoms/Button'
import Card from '@/components/elements/atoms/Card/Card'
import Image from '@/components/elements/atoms/Image/Image'
import Text from '@/components/elements/atoms/Text/Text'
import STYLES from '@/components/pages/PriceSensitivePausedPage/PriceSensitivePausedPage.module.sass'

/**
 * Variant prop - Different layout and copy
 * topImage variant: the image is top aligned on mobile and left aligned on desktop and
 * bottomImage variant: the image is bottom aligned on mobile and right aligned on desktop
 */
type Props = {
  OOBPriceFormatted: string
  variant?: 'topImage' | 'bottomImage'
  identifier?: string
}

const OOBCard = ({
  OOBPriceFormatted,
  variant = 'bottomImage',
  identifier = 'price_sensitive_paused_page.oob_card.review_oob'
}: Props): JSX.Element => {
  const namespace = 'account'
  const { t } = useTranslation('account')
  const navigate = useNavigate()
  const { windowWidth } = useWindowSize()
  const isMobile = windowWidth < BREAKPOINTS.md

  const navigateToOOB = useCallback((): void => {
    navigate(ACCOUNT_ROUTES.oneOffBox)
  }, [navigate])

  const desktopWrapperClasses = classNames(STYLES.desktopOOBWrapper, {
    [STYLES.desktopOOBWrapperTop]: variant === 'topImage'
  })

  const cardContentClasses = classNames(STYLES.cardContent, {
    [STYLES.cardContentTop]: variant === 'topImage'
  })

  const textSpacingClasses = classNames(STYLES.textSpacing, {
    [STYLES.textSpacingXLarge]: variant === 'bottomImage',
    [STYLES.textSpacingLarge]: variant === 'topImage'
  })

  const titleCopy =
    variant === 'bottomImage'
      ? 'price_sensitive_paused_page.options.oob_card.title'
      : 'price_sensitive_paused_page.options.oob_card.title_alternative'

  const subtitleCopy =
    variant === 'bottomImage'
      ? 'price_sensitive_paused_page.options.oob_card.subtitle'
      : 'price_sensitive_paused_page.options.oob_card.subtitle_alternative'

  return (
    <Card border="none" padding={24}>
      <div className={cardContentClasses}>
        {!isMobile ? (
          <div className={STYLES.textSpacing}>
            {variant === 'bottomImage' && (
              <Text
                namespace={namespace}
                text={titleCopy}
                variant="display20"
                margin={false}
              />
            )}
            <div className={desktopWrapperClasses}>
              <div className={textSpacingClasses}>
                <div>
                  {variant === 'topImage' && (
                    <Text
                      namespace={namespace}
                      text={titleCopy}
                      variant="display20"
                      margin={false}
                      element="h4"
                    />
                  )}
                  <Text
                    namespace={namespace}
                    text={subtitleCopy}
                    margin={false}
                  />
                </div>
                <Text
                  namespace={namespace}
                  text={`price_sensitive_paused_page.options.oob_card.price`}
                  variant="display16"
                  margin={false}
                  variables={{ priceOOB: OOBPriceFormatted }}
                  align={variant === 'topImage' ? 'right' : 'left'}
                />
                {variant === 'topImage' && (
                  <Button
                    onClick={navigateToOOB}
                    typography={{
                      namespace,
                      text: `price_sensitive_paused_page.options.oob_card.cta`
                    }}
                    fullWidth
                    identifier="price_sensitive_paused_page.oob_card.review_oob"
                    variant="secondary"
                  />
                )}
              </div>
              <div className={STYLES.oobImageWrap}>
                <Image
                  className={STYLES.oobImage}
                  slug={'oob_review_card_img'}
                  image={{
                    resizeMode: 'resize_to_fill',
                    retina: true,
                    height: variant === 'topImage' ? 175 : 149,
                    width: variant === 'topImage' ? 455 : 184
                  }}
                  alt={t(
                    'price_sensitive_paused_page.options.oob_card.img_alt'
                  )}
                />
              </div>
            </div>
            {variant === 'bottomImage' && (
              <Button
                onClick={navigateToOOB}
                typography={{
                  namespace,
                  text: `price_sensitive_paused_page.options.oob_card.cta`
                }}
                fullWidth
                identifier="price_sensitive_paused_page.oob_card.review_oob"
              />
            )}
          </div>
        ) : (
          <>
            <div className={STYLES.textSpacing}>
              <Text
                namespace={namespace}
                text={titleCopy}
                variant="display20"
                margin={false}
              />
              <Text namespace={namespace} text={subtitleCopy} margin={false} />
            </div>
            <div className={STYLES.boxPrice}>
              <Text
                namespace={namespace}
                text={`price_sensitive_paused_page.options.oob_card.price`}
                variant="display16"
                margin={false}
                variables={{ priceOOB: OOBPriceFormatted }}
                align={variant === 'topImage' ? 'right' : 'left'}
              />
            </div>
            <Button
              onClick={navigateToOOB}
              typography={{
                namespace,
                text: `price_sensitive_paused_page.options.oob_card.cta`
              }}
              fullWidth
              identifier={identifier}
              variant={variant === 'bottomImage' ? 'primary' : 'secondary'}
            />
            <div className={STYLES.oobImageWrap}>
              <Image
                className={STYLES.oobImage}
                slug={'oob_review_card_img'}
                image={{
                  resizeMode: 'resize_to_fill',
                  retina: true,
                  height: 175,
                  width: 318
                }}
                alt={t('price_sensitive_paused_page.options.oob_card.img_alt')}
              />
            </div>
          </>
        )}
      </div>
    </Card>
  )
}

export { OOBCard }
